import type { Clock, Random } from '../clock.ts';
import { err, ok, type Result } from '../result.ts';
import { abortedError, classifyHttpError, networkError, type ApiError } from './errors.ts';
import { backoffDelayMs, DEFAULT_RETRY_POLICY, type RetryPolicy } from './retry.ts';
import { isSuccessStatus, type HttpClient, type HttpRequest, type HttpResponse } from './types.ts';

/**
 * The injected {@link HttpClient} with §3's retry and backoff wrapped around it, and R6's
 * classifier applied to whatever comes back. Errors are values here, not exceptions — the
 * platform's client may throw, this never does.
 */
export interface ApiTransport {
  send(request: HttpRequest): Promise<Result<HttpResponse, ApiError>>;
}

export interface ApiTransportDeps {
  readonly http: HttpClient;
  readonly clock: Clock;
  readonly random: Random;
  readonly retryPolicy?: RetryPolicy;
  /**
   * How a non-2xx becomes an {@link ApiError}. Defaults to {@link classifyHttpError}.
   *
   * It takes the **body** because the authenticated classifier cannot work without it: the
   * instance answers five different auth failures with the same bare `403`, and the transport is
   * the last place the body still exists — by the time a caller sees the `Result` it is gone.
   */
  readonly classifyError?: (status: number, url: string, body: string) => ApiError;
}

/** R10 — no singletons. Each call builds its own transport, so two cores can coexist. */
export function createApiTransport(deps: ApiTransportDeps): ApiTransport {
  const retryPolicy = deps.retryPolicy ?? DEFAULT_RETRY_POLICY;
  const classify = deps.classifyError ?? classifyHttpError;

  return {
    async send(request: HttpRequest): Promise<Result<HttpResponse, ApiError>> {
      // Read through a function: `aborted` flips between awaits, and narrowing it once would let
      // the compiler assume it never changes.
      const isAborted = (): boolean => request.signal?.aborted === true;

      for (let attempt = 1; attempt <= retryPolicy.maxAttempts; attempt += 1) {
        if (isAborted()) return err(abortedError(request.url));

        let failure: ApiError;
        try {
          const response = await deps.http.send(request);
          if (isSuccessStatus(response.status)) return ok(response);
          failure = classify(response.status, request.url, response.body);
        } catch {
          // A CORS rejection and a dead network are the same exception in JS (§3).
          failure = isAborted() ? abortedError(request.url) : networkError(request.url);
        }

        if (!failure.retryable || attempt === retryPolicy.maxAttempts) return err(failure);

        await deps.clock.sleep(backoffDelayMs(attempt, deps.random.next(), retryPolicy), request.signal);
        if (isAborted()) return err(abortedError(request.url));
      }

      // Unreachable: the loop always returns. Kept total rather than relying on the compiler.
      return err(networkError(request.url));
    },
  };
}
